/*!
 * Copyright 2017 - 2020 by ChartIQ, Inc.
 * All rights reserved.
 */

/**
 * API Usage Tracker
 *
 * Tracks which client-side API calls have been used during this session.
 * Each service maintains its own tracker instance for deduplication.
 * When an API is used for the first time, it forwards the usage data to
 * a downstream log service for persistence.
 */

import { Logger } from "../../../clients/logger";
import { ConfigClient } from "../../../clients/configClient";

/**
 * Mapping from service responder channels to user-facing API names.
 * This maps the internal router channel names to what customers would type in console.
 */
const CHANNEL_TO_API_MAP: Record<string, string> = {
	// ============ SearchClient ============
	"Search.register": "FSBL.Clients.SearchClient.register",
	"Search.unregister": "FSBL.Clients.SearchClient.unregister",
	"Search.search": "FSBL.Clients.SearchClient.search",

	// ============ ConfigClient ============
	"configService.getValues": "FSBL.Clients.ConfigClient.get",
	"configService.getValue": "FSBL.Clients.ConfigClient.getValue",
	"configService.setValue": "FSBL.Clients.ConfigClient.setValue",
	"configService.setValues": "FSBL.Clients.ConfigClient.setValues",
	"configService.removeValue": "FSBL.Clients.ConfigClient.removeValue",
	"configService.removeValues": "FSBL.Clients.ConfigClient.removeValues",
	"configService.addListener": "FSBL.Clients.ConfigClient.addListener",
	"configService.processAndSet": "FSBL.Clients.ConfigClient.processAndSet",
	"PreferencesService.setPreference": "FSBL.Clients.ConfigClient.setPreference",
	"PreferencesService.getPreferences": "FSBL.Clients.ConfigClient.getPreferences",
	"PreferencesService.unsetPreference": "FSBL.Clients.ConfigClient.unsetPreference",

	// ============ WindowClient ============
	"DockingService.startTilingOrTabbing": "FSBL.Clients.WindowClient.startTilingOrTabbing",
	"DockingService.cancelTilingOrTabbing": "FSBL.Clients.WindowClient.cancelTilingOrTabbing",
	"DockingService.stopTilingOrTabbing": "FSBL.Clients.WindowClient.stopTilingOrTabbing",
	"DockingService.showAtMousePosition": "FSBL.Clients.WindowClient.showAtMousePosition",

	// ============ DistributedStoreClient ============
	"storeService.getStore": "FSBL.Clients.DistributedStoreClient.getStore",
	"storeService.createStore": "FSBL.Clients.DistributedStoreClient.createStore",
	"storeService.removeStore": "FSBL.Clients.DistributedStoreClient.removeStore",

	// ============ AppsClient (and LauncherClient alias) ============
	"Launcher.componentList": "FSBL.Clients.AppsClient.getComponentList",
	"Launcher.showWindow": "FSBL.Clients.AppsClient.showWindow",
	"Launcher.spawn": "FSBL.Clients.AppsClient.spawn",
	"Launcher.clone": "FSBL.Clients.AppsClient.clone",
	"Launcher.spawnTabCollection": "FSBL.Clients.AppsClient.spawnTabCollection",
	"Launcher.getActiveDescriptors": "FSBL.Clients.AppsClient.getActiveDescriptors",
	"Launcher.userDefinedComponentUpdate": "FSBL.Clients.AppsClient.addUserDefinedComponent",
	"configService.addApps": "FSBL.Clients.AppsClient.addApps",
	"configService.removeApps": "FSBL.Clients.AppsClient.removeApps",
	"configService.getApps": "FSBL.Clients.AppsClient.getApps",

	// ============ StorageClient ============
	"Storage.setUser": "FSBL.Clients.StorageClient.setUser",
	"Storage.setStore": "FSBL.Clients.StorageClient.setStore",
	"Storage.save": "FSBL.Clients.StorageClient.save",
	"Storage.get": "FSBL.Clients.StorageClient.get",
	"Storage.getMultiple": "FSBL.Clients.StorageClient.getMultiple",
	"Storage.keys": "FSBL.Clients.StorageClient.keys",
	"Storage.delete": "FSBL.Clients.StorageClient.remove",
	"Storage.clearCache": "FSBL.Clients.StorageClient.clearCache",

	// ============ WorkspaceClient ============
	"Finsemble.WorkspaceService.getActiveWorkspace": "FSBL.Clients.WorkspaceClient.getActiveWorkspace",
	"Finsemble.WorkspaceService.getWorkspaces": "FSBL.Clients.WorkspaceClient.getWorkspaces",
	"Finsemble.WorkspaceService.getWorkspaceNames": "FSBL.Clients.WorkspaceClient.getWorkspaceNames",
	"Finsemble.WorkspaceService.setWorkspaceOrder": "FSBL.Clients.WorkspaceClient.setWorkspaceOrder",
	"Finsemble.WorkspaceService.remove": "FSBL.Clients.WorkspaceClient.remove",
	"Finsemble.WorkspaceService.rename": "FSBL.Clients.WorkspaceClient.rename",
	"Finsemble.WorkspaceService.save": "FSBL.Clients.WorkspaceClient.save",
	"Finsemble.WorkspaceService.saveAs": "FSBL.Clients.WorkspaceClient.saveAs",
	"Finsemble.WorkspaceService.switchTo": "FSBL.Clients.WorkspaceClient.switchTo",
	"Finsemble.WorkspaceService.setWindowState": "FSBL.Clients.WorkspaceClient.setWindowState",
	"Finsemble.WorkspaceService.getWindowState": "FSBL.Clients.WorkspaceClient.getWindowState",
	"Finsemble.WorkspaceService.getActiveWorkspaceDirty": "FSBL.Clients.WorkspaceClient.isWorkspaceDirty",
	"Finsemble.WorkspaceService.newWorkspace": "FSBL.Clients.WorkspaceClient.createWorkspace",
	"Finsemble.WorkspaceService.export": "FSBL.Clients.WorkspaceClient.exportWorkspace",
	"Finsemble.WorkspaceService.import": "FSBL.Clients.WorkspaceClient.importWorkspace",

	// ============ HotkeyClient ============
	"hotkeysService.registerGlobalHotkey": "FSBL.Clients.HotkeyClient.addGlobalHotkey",
	"hotkeysService.unregisterGlobalHotkey": "FSBL.Clients.HotkeyClient.removeGlobalHotkey",
	"hotkeysService.removeAllHotkeysForWindow": "FSBL.Clients.HotkeyClient.removeAllHotkeys",

	// ============ NotificationClient ============
	"notification.fetch_history": "FSBL.Clients.NotificationClient.fetchHistory",
	"notification.last_issued": "FSBL.Clients.NotificationClient.getLastIssuedAt",
	"notification.perform_action": "FSBL.Clients.NotificationClient.performAction",
	"notification.mark_read": "FSBL.Clients.NotificationClient.markRead",
	"notification.mark_unread": "FSBL.Clients.NotificationClient.markUnread",
	"notification.delete": "FSBL.Clients.NotificationClient.deleteNotifications",
	"notification.notify": "FSBL.Clients.NotificationClient.notify",
	"notification.subscribe": "FSBL.Clients.NotificationClient.subscribe",
	"notification.unsubscribe": "FSBL.Clients.NotificationClient.unsubscribe",
	"notification.mute": "FSBL.Clients.NotificationClient.mute",
	"notification.unmute": "FSBL.Clients.NotificationClient.unmute",
	"notification.sources": "FSBL.Clients.NotificationClient.getSources",
	"notification.mute_filters": "FSBL.Clients.NotificationClient.getMuteFilters",
	"notification.preferences": "FSBL.Clients.NotificationClient.getPreferences",

	// ============ AuthenticationClient ============
	"authentication.currentCredentials": "FSBL.Clients.AuthenticationClient.getCurrentCredentials",
	"authentication.dialogSignOnToAuthService": "FSBL.Clients.AuthenticationClient.transmitSignOnToAuthService",
	"authentication.appSignOn": "FSBL.Clients.AuthenticationClient.appSignOn",
	"authentication.appRejectAndRetrySignOn": "FSBL.Clients.AuthenticationClient.appRejectAndRetrySignOn",
	"authentication.completeOAUTH": "FSBL.Clients.AuthenticationClient.completeOAUTH",
	"authentication.beginAuthentication": "FSBL.Clients.AuthenticationClient.beginAuthentication",

	// ============ FSBL Direct Methods ============
	"Application.shutdown": "FSBL.shutdownApplication",
	"Application.restart": "FSBL.restartApplication",
	"systemManager.showSystemlog": "FSBL.SystemManagerClient.showSystemLog",
	"systemManager.hideSystemlog": "FSBL.SystemManagerClient.hideSystemLog",

	// ============ Desktop Utilities ============
	"DockingService.autoArrange": "FSBL.Clients.WorkspaceClient.autoArrange",
	"LauncherService.minimizeWindows": "FSBL.Clients.WorkspaceClient.minimizeWindows",
	"LauncherService.bringWindowsToFront": "FSBL.Clients.WorkspaceClient.bringWindowsToFront",

	// ============ System API ============
	"FSBL.System.restart": "FSBL.System.restart",
	"FSBL.System.setTrayIcon": "FSBL.System.setTrayIcon",
	"FSBL.System.terminate": "FSBL.System.terminate",
	"FSBL.System.Notification.send": "FSBL.System.Notification.send",
	"FSBL.System.getManifest": "FSBL.System.getManifest",
	"FSBL.System.getMousePosition": "FSBL.System.getMousePosition",
	"FSBL.System.deleteAssets": "FSBL.System.deleteAssets",
	"FSBL.System.getMonitorInfo": "FSBL.System.getMonitorInfo",
	"FSBL.System.getHostSpecs": "FSBL.System.getHostSpecs",
	"FSBL.System.launchExternalProcess": "FSBL.System.launchExternalProcess",
	"FSBL.System.terminateExternalProcess": "FSBL.System.terminateExternalProcess",
	"FSBL.System.clearCache": "FSBL.System.clearCache",
	"FSBL.System.showDeveloperTools": "FSBL.System.showDeveloperTools",
	"FSBL.System.addEventListener": "FSBL.System.addEventListener",
	"FSBL.System.removeEventListener": "FSBL.System.removeEventListener",
	"FSBL.System.addSelectClientCertificateListener": "FSBL.System.addSelectClientCertificateListener",
	"FSBL.System.addCertificateErrorListener": "FSBL.System.addCertificateErrorListener",
	"FSBL.System.getFEAInfo": "FSBL.System.getFEAInfo",
	"FSBL.System.getVersion": "FSBL.System.getVersion",
	"FSBL.System.getUpdateAvailable": "FSBL.System.getUpdateAvailable",
	"FSBL.System.openUrlWithBrowser": "FSBL.System.openUrlWithBrowser",
	"FSBL.System.getProcessList": "FSBL.System.getProcessList",
	"FSBL.System.getProtocolString": "FSBL.System.getProtocolString",
	"FSBL.System.hideSplashScreen": "FSBL.System.hideSplashScreen",
	"FSBL.System.notifyLoggerReady": "FSBL.System.notifyLoggerReady",
	"FSBL.System.updateCentralLoggerState": "FSBL.System.updateCentralLoggerState",
	"FSBL.System.getEnvironmentVariable": "FSBL.System.getEnvironmentVariable",

	// ============ FinsembleWindow (via WindowService) ============
	"WindowService-Request-addEventListener": "finsembleWindow.addEventListener",
	"WindowService-Request-removeEventListener": "finsembleWindow.removeEventListener",
	"WindowService-Request-registerInterruptibleEvent": "finsembleWindow.registerInterruptibleEvent",
	"WindowService-Request-minimize": "finsembleWindow.minimize",
	"WindowService-Request-maximize": "finsembleWindow.maximize",
	"WindowService-Request-restore": "finsembleWindow.restore",
	"WindowService-Request-focus": "finsembleWindow.focus",
	"WindowService-Request-blur": "finsembleWindow.blur",
	"WindowService-Request-bringToFront": "finsembleWindow.bringToFront",
	"WindowService-Request-saveWindowOptions": "finsembleWindow.saveWindowOptions",
	"WindowService-Request-setIgnoreMouseEvents": "finsembleWindow.setIgnoreMouseEvents",
	"WindowService-Request-showNativeOpenDialog": "finsembleWindow.showNativeOpenDialog",
	"WindowService-Request-setShape": "finsembleWindow.setShape",
	"WindowService-Request-setBounds": "finsembleWindow.setBounds",
	"WindowService-Request-getBounds": "finsembleWindow.getBounds",
	"WindowService-Request-addBrowserView": "finsembleWindow.addBrowserView",
	"WindowService-Request-removeBrowserView": "finsembleWindow.removeBrowserView",
	"WindowService-Request-reload": "finsembleWindow.reload",
	"WindowService-Request-setViewBounds": "finsembleWindow.setViewBounds",
	"WindowService-Request-bringViewToFront": "finsembleWindow.bringViewToFront",
	"WindowService-Request-showBrowserView": "finsembleWindow.showBrowserView",
	"WindowService-Request-hideBrowserView": "finsembleWindow.hideBrowserView",
	"WindowService-Request-getOptions": "finsembleWindow.getOptions",
	"WindowService-Request-updateOptions": "finsembleWindow.updateOptions",
	"WindowService-Request-hide": "finsembleWindow.hide",
	"WindowService-Request-show": "finsembleWindow.show",
	"WindowService-Request-showAt": "finsembleWindow.showAt",
	"WindowService-Request-SET_ALWAYS_ON_TOP": "finsembleWindow.setAlwaysOnTop",
	"WindowService-Request-IS_ALWAYS_ON_TOP": "finsembleWindow.isAlwaysOnTop",
	"WindowService-Request-setOpacity": "finsembleWindow.setOpacity",
	"WindowService-Request-close": "finsembleWindow.close",
	"WindowService-Request-isShowing": "finsembleWindow.isShowing",
	"WindowService-Request-animate": "finsembleWindow.animate",
	"WindowService-Request-setComponentState": "finsembleWindow.setComponentState",
	"WindowService-Request-removeComponentState": "finsembleWindow.removeComponentState",
	"WindowService-Request-setWindowState": "finsembleWindow.setWindowState",
	"WindowService-Request-getWindowState": "finsembleWindow.getWindowState",
	"WindowService-Request-getComponentState": "finsembleWindow.getComponentState",
	"WindowService-Request-saveCompleteWindowState": "finsembleWindow.saveCompleteWindowState",
	"DockingService.getMonitorForWindow": "finsembleWindow.getMonitor",
};

export class ApiUsageTracker {
	private trackedApis: Set<string> = new Set();

	private enabled: boolean = false;

	constructor() {
		// Asynchronously check if API tracking is enabled via config
		this.initializeTrackingConfig();
	}

	/**
	 * Initialize tracking configuration from ConfigClient.
	 * This is async, so early API calls before config loads won't be tracked.
	 */
	private async initializeTrackingConfig(): Promise<void> {
		try {
			const { err, data } = await ConfigClient.get(["finsemble", "trackAPIUsage"]);
			if (!err && typeof data === "boolean") {
				this.enabled = data;
				if (this.enabled) {
					Logger.system.debug("[ApiUsageTracker] API usage tracking enabled");
				}
			}
		} catch (error) {
			// If config read fails, tracking remains disabled (default)
			Logger.system.debug("[ApiUsageTracker] Could not read trackAPIUsage config, tracking disabled");
		}
	}

	/**
	 * Track usage of an API by its router channel name.
	 * If this is the first time the API is being used this session,
	 * forward it to the log service.
	 *
	 * @param channel - The router channel name (e.g., "Search.register")
	 */
	track(channel: string): void {
		// Skip if tracking is disabled
		if (!this.enabled) {
			return;
		}

		// Skip if already tracked
		if (this.trackedApis.has(channel)) {
			return;
		}

		// Mark as tracked
		this.trackedApis.add(channel);

		// Get the user-facing API name
		const apiName = CHANNEL_TO_API_MAP[channel];

		if (!apiName) {
			Logger.system.warn(`API Logging -  No mapping found for channel: ${channel}`);
			return;
		}

		// Forward to log service
		this.forwardToLogService(apiName);
	}

	/**
	 * Forward API usage to the downstream log service.
	 *
	 * @param apiName - The user-facing API name (e.g., "FSBL.Clients.SearchClient.register")
	 */
	private forwardToLogService(apiName: string): void {
		Logger.system.log(`API Logging - ${apiName}`);
	}
}
